import React from 'react';
import { FlatList, Pressable, View } from 'react-native';
import { YStack, XStack, H5, SizableText } from 'tamagui';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import getDaysAgo from '@utils/getDaysAgo';
import moment from 'moment';
import * as Animatable from 'react-native-animatable';

interface Thread {
  _id: string;
  title: string;
  content?: string;
  topicId: string;
  replies: number;
  views: number;
  likes: number;
  isSticky?: boolean;
  isLocked?: boolean;
  isSolved?: boolean;
  tags?: string[];
  createdBy: {
    _id: string;
    name: string;
    surname: string;
    avatar?: string;
    role?: string;
  };
  createdAt: string;
  lastReply?: {
    user: {
      name: string;
      avatar?: string;
    };
    createdAt: string;
  };
  status?: 'open' | 'closed' | 'solved';
}

interface ThreadsListProps {
  threads: Thread[];
  loading: boolean;
  onThreadSelect: (threadId: string) => void;
  selectedThread?: string | null;
  topicTitle?: string;
  compact?: boolean;
}

const ThreadItem: React.FC<{
  thread: Thread;
  onPress: () => void;
  isSelected?: boolean;
  compact?: boolean;
}> = ({ thread, onPress, isSelected = false, compact = false }) => {
  const theme = themeSelector();
  
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    selectedBackground: theme ? '#e0f2fe' : '#1e3a8a',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#3b82f6',
    success: '#10b981',
    warning: '#f59e0b',
    danger: '#ef4444',
  };

  const getStatusColor = () => {
    if (thread.isSolved) return colors.success;
    if (thread.isLocked) return colors.danger;
    if (thread.status === 'closed') return colors.warning;
    return colors.accent;
  };

  const getStatusIcon = () => {
    if (thread.isSolved) return 'check-circle';
    if (thread.isLocked) return 'lock';
    if (thread.status === 'closed') return 'archive';
    return 'message-text';
  };

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      delay={compact ? 0 : 50}
    >
      <Pressable
        onPress={onPress}
        style={({ pressed }) => ({
          backgroundColor: isSelected 
            ? colors.selectedBackground 
            : pressed 
              ? colors.cardBackground 
              : colors.background,
          marginHorizontal: compact ? 6 : 12,
          marginVertical: compact ? 3 : 6,
          borderRadius: 10,
          borderWidth: 1,
          borderColor: isSelected ? colors.accent : colors.border,
          borderLeftWidth: 4,
          borderLeftColor: getStatusColor(),
          padding: compact ? 10 : 14,
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.05,
          shadowRadius: 2,
          elevation: 1,
          opacity: pressed ? 0.8 : 1,
        })}
      >
        <XStack gap="$2" alignItems="flex-start">
          {/* Status Indicator */}
          <View style={{
            width: compact ? 32 : 36,
            height: compact ? 32 : 36,
            backgroundColor: `${getStatusColor()}15`,
            borderRadius: compact ? 16 : 18,
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: 2,
          }}>
            <MaterialCommunityIcons 
              name={getStatusIcon() as any} 
              size={compact ? 16 : 18} 
              color={getStatusColor()} 
            />
          </View>

          {/* Content */}
          <YStack flex={1} gap="$1">
            <XStack justifyContent="space-between" alignItems="flex-start">
              <H5 
                color={colors.text} 
                numberOfLines={compact ? 1 : 2}
                fontSize={compact ? "$3" : "$4"}
                fontWeight="600"
                flex={1}
                marginRight="$2"
              >
                {thread.isSticky && (
                  <MaterialIcons 
                    name="push-pin" 
                    size={14} 
                    color={colors.warning} 
                  />
                )}
                {thread.title}
                {thread.isLocked && (
                  <MaterialIcons 
                    name="lock" 
                    size={14} 
                    color={colors.danger} 
                    style={{ marginLeft: 6 }}
                  />
                )}
              </H5>

              {/* Quick Stats */}
              <XStack gap="$1" alignItems="center" opacity={0.7}>
                <MaterialCommunityIcons 
                  name="eye" 
                  size={12} 
                  color={colors.textSecondary} 
                />
                <SizableText color={colors.textSecondary} size="$1">
                  {thread.views}
                </SizableText>
              </XStack>
            </XStack>

            {/* Tags */}
            {thread.tags && thread.tags.length > 0 && !compact && (
              <XStack gap="$1" marginTop="$1" flexWrap="wrap">
                {thread.tags.slice(0, 3).map((tag, index) => (
                  <View
                    key={index}
                    style={{
                      backgroundColor: `${colors.accent}20`,
                      paddingHorizontal: 8,
                      paddingVertical: 2,
                      borderRadius: 4,
                    }}
                  >
                    <SizableText color={colors.accent} size="$1">
                      {tag}
                    </SizableText>
                  </View>
                ))}
                {thread.tags.length > 3 && (
                  <SizableText color={colors.textSecondary} size="$1">
                    +{thread.tags.length - 3} more
                  </SizableText>
                )}
              </XStack>
            )}

            {/* Content Preview */}
            {thread.content && !compact && (
              <SizableText 
                color={colors.textSecondary} 
                size="$2" 
                numberOfLines={2}
                lineHeight={16}
                marginTop="$1"
              >
                {thread.content}
              </SizableText>
            )}

            {/* Stats Row */}
            <XStack 
              justifyContent="space-between" 
              alignItems="center" 
              marginTop={compact ? "$1" : "$2"}
            >
              <XStack gap="$3" alignItems="center">
                <XStack gap="$1" alignItems="center">
                  <MaterialCommunityIcons 
                    name="comment-multiple" 
                    size={12} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$1">
                    {thread.replies}
                  </SizableText>
                </XStack>

                <XStack gap="$1" alignItems="center">
                  <MaterialCommunityIcons 
                    name="heart" 
                    size={12} 
                    color={colors.textSecondary} 
                  />
                  <SizableText color={colors.textSecondary} size="$1">
                    {thread.likes}
                  </SizableText>
                </XStack>
              </XStack>

              <XStack alignItems="center" gap="$2">
                <SizableText color={colors.textSecondary} size="$1">
                  {getDaysAgo(thread.createdAt)}
                </SizableText>
                {thread.createdBy.role && (
                  <View
                    style={{
                      backgroundColor: colors.cardBackground,
                      paddingHorizontal: 4,
                      paddingVertical: 1,
                      borderRadius: 3,
                    }}
                  >
                    <SizableText color={colors.textSecondary} size="$1">
                      {thread.createdBy.role}
                    </SizableText>
                  </View>
                )}
              </XStack>
            </XStack>

            {/* Author & Last Reply */}
            <XStack 
              justifyContent="space-between" 
              alignItems="center" 
              marginTop="$1"
              opacity={0.8}
            >
              <XStack alignItems="center" gap="$1" flex={1}>
                <MaterialCommunityIcons 
                  name="account-circle" 
                  size={14} 
                  color={colors.textSecondary} 
                />
                <SizableText color={colors.textSecondary} size="$1" numberOfLines={1}>
                  {thread.createdBy.name} {thread.createdBy.surname}
                </SizableText>
              </XStack>

              {thread.lastReply && (
                <SizableText color={colors.textSecondary} size="$1">
                  Last: {getDaysAgo(thread.lastReply.createdAt)}
                </SizableText>
              )}
            </XStack>
          </YStack>
        </XStack>
      </Pressable>
    </Animatable.View>
  );
};

const ThreadsList: React.FC<ThreadsListProps> = ({
  threads,
  loading,
  onThreadSelect,
  selectedThread,
  topicTitle,
  compact = false
}) => {
  const theme = themeSelector();
  
  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
  };

  if (loading) {
    return (
      <YStack padding="$4" gap="$4" alignItems="center">
        <MaterialCommunityIcons 
          name="loading" 
          size={28} 
          color={colors.textSecondary} 
        />
        <SizableText color={colors.textSecondary}>Loading discussions...</SizableText>
      </YStack>
    );
  }

  if (!threads || threads.length === 0) {
    return (
      <YStack 
        padding="$6" 
        gap="$4" 
        alignItems="center"
        justifyContent="center"
        flex={1}
      >
        <MaterialCommunityIcons 
          name="message-text-outline" 
          size={56} 
          color={colors.textSecondary} 
        />
        <H5 color={colors.text} textAlign="center">No Discussions Yet</H5>
        <SizableText 
          color={colors.textSecondary} 
          textAlign="center"
          maxWidth={260}
        >
          {topicTitle 
            ? `Start the first discussion in ${topicTitle}!`
            : 'Be the first to start a discussion!'
          }
        </SizableText>
      </YStack>
    );
  }

  const renderThread = ({ item }: { item: Thread }) => (
    <ThreadItem
      thread={item}
      onPress={() => onThreadSelect(item._id)}
      isSelected={selectedThread === item._id}
      compact={compact}
    />
  );

  return (
    <FlatList
      data={threads}
      renderItem={renderThread}
      keyExtractor={(item) => item._id}
      contentContainerStyle={{
        paddingVertical: compact ? 6 : 12,
        backgroundColor: colors.background,
        flexGrow: 1,
      }}
      showsVerticalScrollIndicator={false}
      removeClippedSubviews
      maxToRenderPerBatch={8}
      windowSize={8}
    />
  );
};

export default ThreadsList;
